import React, { useMemo } from 'react';
import { CheckCircle, AlertTriangle, Layers, Zap, BarChart3 } from 'lucide-react';
import { validateSeedCoverage, validateSeedQuestionsLocal, getAllSeedQuestions, getSeedQuestionTypeDistribution, SEED_COLLECTIONS, getSeedCollection, TAXONOMY, FAMILIES_BY_MODULE, MODULES, type ModuleId } from '../../../../content';
import { QUESTION_TYPE_META } from '../../../../content/questions/questionTypeRegistry';
import type { QuestionType } from '../../../../content/types';
import { getModuleHexColor } from '../../../../content/moduleColors';
export function SeedStatus() {
  const coverageReport = useMemo(() => validateSeedCoverage(), []);
  const validationReport = useMemo(() => validateSeedQuestionsLocal(), []);
  const typeDistribution = useMemo(() => getSeedQuestionTypeDistribution(), []);
  const coveragePercent = Math.round(coverageReport.coveredFamilies / coverageReport.totalFamilies * 100);
  const sortedTypes = useMemo(() => {
    return Object.entries(typeDistribution).sort((a, b) => b[1] - a[1]);
  }, [typeDistribution]);
  return <div className="space-y-6 sm:space-y-8" data-id="element-6905">
      {/* 1. Module Overzicht */}
      <div data-id="element-6906">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-6907">
          <BarChart3 className="w-5 h-5 text-blue-600" data-id="element-6908" />
          Module Overzicht
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-6909">
          {MODULES.map(module => {
          const collection = SEED_COLLECTIONS.get(module.id);
          const families = FAMILIES_BY_MODULE.get(module.id) || [];
          const questionCount = collection?.questions.length || 0;
          const familyCount = families.length;
          const moduleFamilyIds = families.map(f => f.familyId);
          const coveredModuleFamilies = new Set(collection?.questions.map(q => q.familyId) || []);
          const validCoveredCount = moduleFamilyIds.filter(id => coveredModuleFamilies.has(id)).length;
          const coverage = familyCount > 0 ? Math.round(validCoveredCount / familyCount * 100) : 0;
          const tiers = collection?.questions.map(q => q.tier).filter(Boolean) || [];
          const minTier = tiers.length > 0 ? Math.min(...tiers) : 0;
          const maxTier = tiers.length > 0 ? Math.max(...tiers) : 0;
          return <div key={module.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col" style={{
            borderLeftWidth: '4px',
            borderLeftColor: getModuleColor(module.id)
          }} data-id="element-6910">
                <div className="p-4 flex-1" data-id="element-6911">
                  <div className="flex justify-between items-start mb-4" data-id="element-6912">
                    <h4 className="font-bold text-gray-900" data-id="element-6913">{module.title}</h4>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-md" data-id="element-6914">
                      {collection?.moduleCode || module.id.substring(0, 3).toUpperCase()}
                    </span>
                  </div>

                  <div className="space-y-2" data-id="element-6915">
                    <div className="flex justify-between text-sm" data-id="element-6916">
                      <span className="text-gray-500" data-id="element-6917">Vragen</span>
                      <span className="font-mono font-medium text-gray-900" data-id="element-6918">
                        {questionCount}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-6919">
                      <span className="text-gray-500" data-id="element-6920">Families</span>
                      <span className="font-mono font-medium text-gray-900" data-id="element-6921">
                        {familyCount}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-6922">
                      <span className="text-gray-500" data-id="element-6923">Tier bereik</span>
                      <span className="font-mono font-medium text-gray-900" data-id="element-6924">
                        {minTier > 0 ? `${minTier}-${maxTier}` : '-'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100" data-id="element-6925">
                  <div className="flex justify-between text-xs mb-1.5" data-id="element-6926">
                    <span className="text-gray-500 font-medium" data-id="element-6927">Dekking</span>
                    <span className={`font-bold ${coverage === 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-6928">
                      {coverage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-6929">
                    <div className={`h-1.5 rounded-full ${coverage === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{
                  width: `${coverage}%`
                }} data-id="element-6930" />
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-6931">
        {/* 2. Dekkingsstatus */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-6932">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-6933">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-6934">
              <CheckCircle className="w-4 h-4 text-emerald-600" data-id="element-6935" />
              Dekkingsstatus
            </h3>
          </div>
          <div className="p-4 sm:p-5" data-id="element-6936">
            <div className="mb-6" data-id="element-6937">
              <div className="flex justify-between items-end mb-2" data-id="element-6938">
                <span className="text-sm font-medium text-gray-600" data-id="element-6939">
                  Totale dekking
                </span>
                <span className={`text-2xl font-bold ${coveragePercent === 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-6940">
                  {coveragePercent}%
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden" data-id="element-6941">
                <div className={`h-full transition-all duration-500 ${coveragePercent === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{
                width: `${coveragePercent}%`
              }} data-id="element-6942" />
              </div>
            </div>

            {coverageReport.missingFamilies.length > 0 ? <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-id="element-6943">
                <h4 className="text-sm font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-6944">
                  <AlertTriangle className="w-4 h-4" data-id="element-6945" />
                  Ontbrekende families ({coverageReport.missingFamilies.length})
                </h4>
                <div className="space-y-2 max-h-60 overflow-y-auto pr-2" data-id="element-6946">
                  {coverageReport.missingFamilies.map(id => <div key={id} className="flex items-center justify-between bg-white p-2 rounded border border-amber-100 text-sm" data-id="element-6947">
                      <span className="font-mono text-amber-900" data-id="element-6948">{id}</span>
                    </div>)}
                </div>
              </div> : <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3" data-id="element-6949">
                <div className="bg-emerald-100 p-2 rounded-full flex-shrink-0" data-id="element-6950">
                  <CheckCircle className="w-5 h-5 text-emerald-600" data-id="element-6951" />
                </div>
                <div data-id="element-6952">
                  <div className="font-bold text-emerald-900" data-id="element-6953">
                    Alle families gedekt
                  </div>
                  <div className="text-sm text-emerald-700" data-id="element-6954">
                    Alle {coverageReport.totalFamilies} families uit de
                    taxonomie hebben minimaal één seed vraag.
                  </div>
                </div>
              </div>}
          </div>
        </div>

        {/* 3. Vraagtypen */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-6955">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-6956">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-6957">
              <Layers className="w-4 h-4 text-purple-600" data-id="element-6958" />
              Vraagtypen
            </h3>
          </div>
          <div className="p-4 sm:p-5" data-id="element-6959">
            <div className="space-y-3" data-id="element-6960">
              {sortedTypes.map(([type, count]) => <div key={type} className="flex items-center justify-between" data-id="element-6961">
                  <div className="flex items-center gap-2" data-id="element-6962">
                    <CheckCircle className="w-4 h-4 text-emerald-500" data-id="element-6963" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-6964">
                      {translateQuestionType(type)}
                    </span>
                  </div>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full" data-id="element-6965">
                    {count}
                  </span>
                </div>)}
              {sortedTypes.length === 0 && <div className="text-sm text-gray-500 text-center py-4" data-id="element-6966">
                  Geen vraagtypen gevonden
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* 4. Validatie */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-6967">
        <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center" data-id="element-6968">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-6969">
            <Zap className="w-4 h-4 text-amber-500" data-id="element-6970" />
            Validatie Resultaten
          </h3>
          <div className="flex gap-2 text-xs font-medium" data-id="element-6971">
            <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full" data-id="element-6972">
              {validationReport.valid} Geldig
            </span>
            {validationReport.invalid > 0 && <span className="px-2.5 py-1 bg-rose-100 text-rose-700 rounded-full" data-id="element-6973">
                {validationReport.invalid} Ongeldig
              </span>}
          </div>
        </div>
        <div className="p-4 sm:p-5" data-id="element-6974">
          {validationReport.issues.length > 0 ? <div className="space-y-3" data-id="element-6975">
              {validationReport.issues.map(issue => <div key={issue.id} className="bg-rose-50 border border-rose-200 rounded-lg p-3 sm:p-4" data-id="element-6976">
                  <div className="font-mono text-sm font-bold text-rose-800 mb-2" data-id="element-6977">
                    {issue.id}
                  </div>
                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-6978">
                    {issue.problems.map((p, i) => <li key={i} data-id="element-6979">{p}</li>)}
                  </ul>
                </div>)}
            </div> : <div className="text-center py-8" data-id="element-6980">
              <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-3" data-id="element-6981" />
              <p className="text-gray-600 font-medium" data-id="element-6982">
                Alle seed vragen zijn succesvol gevalideerd
              </p>
            </div>}
        </div>
      </div>
    </div>;
}
// Helpers
function getModuleColor(moduleId: ModuleId): string {
  const mod = MODULES.find(m => m.id === moduleId);
  if (!mod) return '#64748b';
  return getModuleHexColor(mod.color);
}
// F-A4 (19 May 2026): use QUESTION_TYPE_META as the SSOT for Dutch labels
// instead of a hand-maintained local map. The old map covered only 14 of
// the 38 registered types — anything outside that set rendered raw English
// keys (e.g. `text_highlight`, `verb_conjugation_flow`) and drifted out of
// sync as new types were added. Audit §19.2.4 / §20.1.
function translateQuestionType(type: string): string {
  const meta = QUESTION_TYPE_META[type as QuestionType];
  return meta?.label ?? type;
}